import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, Layout, Layers, Info, GitBranch, Plus, Trash2, ChevronDown, ChevronUp, Image as ImageIcon, Eye, EyeOff, Loader2, FileText, Settings, Mail, Building, DollarSign, Calendar, MessageSquare, Users, Shield, Search, Award, BookOpen, Code, FileCode } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from "../../lib/api";
import { clearAdminSession, isTokenValid } from "../../lib/auth";
import InputGroup from "./InputGroup";

const baseSections = [
  { id: "hero", label: "Home Screen", description: "Edit the big text at the top", icon: <Layout className="w-5 h-5" /> },
  { id: "about", label: "About Us", description: "Who we are and stats", icon: <Info className="w-5 h-5" /> },
  { id: "trusted_by", label: "Trusted By", description: "Industry leaders marquee", icon: <Award className="w-5 h-5" /> },
  { id: "services", label: "Services", description: "What we offer", icon: <Layers className="w-5 h-5" /> },
  { id: "process", label: "Our Process", description: "How we work", icon: <GitBranch className="w-5 h-5" /> },
  { id: "quote_requests", label: "Quote Requests", description: "View incoming quotes", icon: <FileText className="w-5 h-5" /> },
  { id: "contacts", label: "Contact Inbox", description: "Messages from the contact form", icon: <Mail className="w-5 h-5" /> },
  { id: "quote_settings", label: "Quote Settings", description: "Customize quote form", icon: <Settings className="w-5 h-5" /> },
  { id: "ticker", label: "Scrolling Text", description: "Edit the scrolling banner", icon: <MessageSquare className="w-5 h-5" /> },
  { id: "projects", label: "Projects", description: "Manage portfolio projects", icon: <ImageIcon className="w-5 h-5" /> },
  { id: "blogs", label: "Blog", description: "Manage blog posts", icon: <BookOpen className="w-5 h-5" /> },
  { id: "footer", label: "Footer", description: "Copyright year and links", icon: <Code className="w-5 h-5" /> },
];

const superadminSections = [
  { id: "users", label: "Users", description: "Manage admin users", icon: <Users className="w-5 h-5" />, superadminOnly: true },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("hero");
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [quoteSearch, setQuoteSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [originalProjects, setOriginalProjects] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [originalBlogs, setOriginalBlogs] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [adminRole, setAdminRole] = useState("");
  const [users, setUsers] = useState([]);

  const readOnlySections = ["quote_requests", "contacts", "users"];
  const sections = adminRole === "superadmin" ? [...baseSections, ...superadminSections] : baseSections;

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const role = localStorage.getItem("admin_role");
    if (!isTokenValid(token)) {
      clearAdminSession();
      navigate("/admin/login");
      return;
    }
    setAdminRole(role || "admin");
    fetchContent(activeSection);
  }, [activeSection, navigate]);

  // Restore normal cursor on admin pages
  useEffect(() => {
    document.body.classList.add('admin-cursor');
    return () => document.body.classList.remove('admin-cursor');
  }, []);

  const fetchContent = async (sectionId) => {
    setLoading(true);
    setExpandedItems({});
    const token = localStorage.getItem("admin_token");
    try {
      if (sectionId === "users") {
        const res = await api.get(`/admins`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(res.data || []);
        setContent({});
      } else if (sectionId === "quote_requests") {
        const res = await api.get("/quotes");
        setQuotes(res.data || []);
        setContent({});
      } else if (sectionId === "contacts") {
        const res = await api.get("/contacts");
        setContacts(res.data || []);
        setContent({});
      } else if (sectionId === "projects") {
        const res = await api.get(`/projects`);
        setProjects(res.data || []);
        setOriginalProjects(res.data || []);
        setContent({});
      } else if (sectionId === "blogs") {
        const res = await api.get(`/blogs`);
        const blogsData = (res.data || []).map(blog => ({
          ...blog,
          content_format: blog.content_format || 'markdown' // Default to markdown
        }));
        setBlogs(blogsData);
        setOriginalBlogs(blogsData);
        setEditingBlog(null);
        setContent({});
      } else if (sectionId === "quote_settings") {
        const res = await api.get(`/content/${sectionId}`);
        const savedContent = res.data.content || {};
        // Pre-populate with defaults if not set
        const defaultProjectTypes = ["Web Development", "Mobile App", "UI/UX Design", "Branding", "E-commerce", "Other"];
        const defaultBudgetRanges = ["< ₹4 Lakhs", "₹4L - ₹8L", "₹8L - ₹20L", "₹20L - ₹40L", "₹40 Lakhs+"];
        setContent({
          title: savedContent.title || "Let's start with the basics.",
          success_message: savedContent.success_message || "We'll analyze your requirements and get back to you within 24 hours.",
          project_types: savedContent.project_types?.length > 0 ? savedContent.project_types : defaultProjectTypes,
          budget_ranges: savedContent.budget_ranges?.length > 0 ? savedContent.budget_ranges : defaultBudgetRanges,
        });
      } else if (sectionId === "ticker") {
        const res = await api.get(`/content/${sectionId}`);
        const savedContent = res.data.content || {};
        const defaultReviews = [
          "Visionary design team.", "Exceptional technical execution.", "Unmatched quality.", "Future-proof development."
        ];
        setContent({
          texts: savedContent.texts?.length > 0 ? savedContent.texts : defaultReviews,
          speed: savedContent.speed || 20
        });
      } else if (sectionId === "footer") {
        const res = await api.get(`/content/${sectionId}`);
        const savedContent = res.data.content || {};
        setContent({
          year: savedContent.year || new Date().getFullYear(),
          company_name: savedContent.company_name || "XLUMA.IN",
          social_links: savedContent.social_links || [
            { name: "Instagram", url: "#" },
            { name: "Twitter", url: "#" },
            { name: "LinkedIn", url: "#" }
          ]
        });
      } else if (sectionId === "trusted_by") {
        const res = await api.get(`/content/${sectionId}`);
        const savedContent = res.data.content || {};
        const defaultClients = ["Google", "Spotify", "Nike", "Adidas", "Tesla", "SpaceX", "Linear", "Vercel"];
        setContent({
          enabled: savedContent.enabled !== false,
          title: savedContent.title || "Trusted by Industry Leaders",
          clients: savedContent.clients?.length > 0 ? savedContent.clients : defaultClients
        });
      } else {
        const res = await api.get(`/content/${sectionId}`);
        setContent(res.data.content || {});
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    navigate("/admin/login");
  };

  const handleSave = async () => {
    if (readOnlySections.includes(activeSection)) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      if (activeSection === "projects") {
        // Find deleted projects
        const currentIds = projects.map(p => p.id);
        const deletedProjects = originalProjects.filter(op => !currentIds.includes(op.id));

        // Delete removed projects
        for (const dp of deletedProjects) {
          await api.delete(`/projects/${dp.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        // Save each project (create new or update existing)
        for (const p of projects) {
          if (p.id.startsWith('temp-')) {
            // New project - POST
            const { id, ...projectData } = p;
            await api.post(`/projects`, projectData, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Existing project - PUT
            await api.put(`/projects/${p.id}`, p, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        }
        // Refresh projects after save
        const res = await api.get(`/projects`);
        setProjects(res.data || []);
        setOriginalProjects(res.data || []);
      } else if (activeSection === "blogs") {
        // Find deleted blogs
        const currentIds = blogs.map(b => b.id);
        const deletedBlogs = originalBlogs.filter(ob => !currentIds.includes(ob.id));

        // Delete removed blogs
        for (const db of deletedBlogs) {
          await api.delete(`/blogs/${db.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        // Save each blog (create new or update existing)
        for (const b of blogs) {
          // Convert structured markdown to markdown string
          let blogData = { ...b };
          if (b.content_format === 'markdown' && (b.markdown_title || b.markdown_intro || b.markdown_sections)) {
            let markdown = '';
            if (b.markdown_title) markdown += `# ${b.markdown_title}\n\n`;
            if (b.markdown_intro) markdown += `${b.markdown_intro}\n\n`;
            (b.markdown_sections || []).forEach(section => {
              if (section.type === 'heading') markdown += `## ${section.content}\n\n`;
              else if (section.type === 'subheading') markdown += `### ${section.content}\n\n`;
              else if (section.type === 'paragraph') markdown += `${section.content}\n\n`;
              else if (section.type === 'list') {
                (section.items || []).forEach(item => {
                  if (item) markdown += `- ${item}\n`;
                });
                markdown += '\n';
              }
              else if (section.type === 'numbered') {
                (section.items || []).forEach((item, i) => {
                  if (item) markdown += `${i + 1}. ${item}\n`;
                });
                markdown += '\n';
              }
              else if (section.type === 'quote') markdown += `> ${section.content}\n\n`;
              else if (section.type === 'code') markdown += `\`\`\`\n${section.content}\n\`\`\`\n\n`;
            });
            blogData.content = markdown;
          }

          if (b.id.startsWith('temp-')) {
            // New blog - POST
            const { id, markdown_title, markdown_intro, markdown_sections, ...dataToSend } = blogData;
            await api.post(`/blogs`, dataToSend, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Existing blog - PUT
            const { markdown_title, markdown_intro, markdown_sections, ...dataToSend } = blogData;
            await api.put(`/blogs/${b.id}`, dataToSend, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
        }
        // Refresh blogs after save
        const res = await api.get(`/blogs`);
        const blogsData = (res.data || []).map(blog => ({
          ...blog,
          content_format: blog.content_format || 'markdown'
        }));
        setBlogs(blogsData);
        setOriginalBlogs(blogsData);
        setEditingBlog(null);
      } else {
        await api.post(
          `/content/${activeSection}`,
          content,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success("Saved! Your website is updated.");
    } catch (error) {
      console.error(error);
      toast.error("Oops, couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const updateListItem = (listKey, index, field, value) => {
    const newList = [...(content[listKey] || [])];
    newList[index] = { ...newList[index], [field]: value };
    setContent({ ...content, [listKey]: newList });
  };

  const addListItem = (listKey, initialItem) => {
    const newList = [...(content[listKey] || []), initialItem];
    setContent({ ...content, [listKey]: newList });
    setExpandedItems(prev => ({ ...prev, [`${listKey}-${newList.length - 1}`]: true }));
  };

  const removeListItem = (listKey, index) => {
    const newList = [...(content[listKey] || [])].filter((_, i) => i !== index);
    setContent({ ...content, [listKey]: newList });
  };

  const toggleExpand = (key) => {
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const deleteQuote = async (quoteId) => {
    if (!window.confirm("Are you sure you want to delete this quote request?")) return;
    
    try {
      const token = localStorage.getItem("admin_token");
      await api.delete(`/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(quotes.filter(q => q.id !== quoteId));
      toast.success("Quote request deleted");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete quote request");
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;

    try {
      await api.delete(`/contacts/${contactId}`);
      setContacts(contacts.filter(c => c.id !== contactId));
      toast.success("Message deleted");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete message");
    }
  };

  // --- EDITOR RENDERERS ---

  const renderHeroEditor = () => (
    <div className="space-y-6 max-w-2xl">
      <InputGroup label="Main Headline (Top)" description="The first big word (e.g., DIGITAL)">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">Show on website</span>
          <button
            onClick={() => handleChange("title_line_1_enabled", content.title_line_1_enabled === false ? true : false)}
            className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${content.title_line_1_enabled !== false
              ? 'bg-accent text-black'
              : 'bg-white/10 text-gray-500'
              }`}
          >
            {content.title_line_1_enabled !== false ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <input
          value={content.title_line_1 || ""}
          onChange={(e) => handleChange("title_line_1", e.target.value)}
          disabled={content.title_line_1_enabled === false}
          className={`w-full bg-black border border-white/20 p-4 rounded-lg text-xl text-white focus:border-accent outline-none font-display font-bold ${content.title_line_1_enabled === false ? 'opacity-50' : ''}`}
          placeholder="DIGITAL"
        />
      </InputGroup>

      <InputGroup label="Main Headline (Bottom)" description="The second big word (e.g., ALCHEMY)">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">Show on website</span>
          <button
            onClick={() => handleChange("title_line_2_enabled", content.title_line_2_enabled === false ? true : false)}
            className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${content.title_line_2_enabled !== false
              ? 'bg-accent text-black'
              : 'bg-white/10 text-gray-500'
              }`}
          >
            {content.title_line_2_enabled !== false ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <input
          value={content.title_line_2 || ""}
          onChange={(e) => handleChange("title_line_2", e.target.value)}
          disabled={content.title_line_2_enabled === false}
          className={`w-full bg-black border border-white/20 p-4 rounded-lg text-xl text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500 focus:border-accent outline-none font-display font-bold ${content.title_line_2_enabled === false ? 'opacity-50' : ''}`}
          placeholder="ALCHEMY"
        />
      </InputGroup>

      <InputGroup label="Subtitle" description="The small text below the headline">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">Show on website</span>
          <button
            onClick={() => handleChange("subtext_enabled", content.subtext_enabled === false ? true : false)}
            className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${content.subtext_enabled !== false
              ? 'bg-accent text-black'
              : 'bg-white/10 text-gray-500'
              }`}
          >
            {content.subtext_enabled !== false ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        <textarea
          value={content.subtext || ""}
          onChange={(e) => handleChange("subtext", e.target.value)}
          disabled={content.subtext_enabled === false}
          rows="3"
          className={`w-full bg-black border border-white/20 p-4 rounded-lg text-white focus:border-accent outline-none resize-none ${content.subtext_enabled === false ? 'opacity-50' : ''}`}
          placeholder="We craft immersive experiences..."
        />
      </InputGroup>

      {/* Video Background Settings */}
      <div className="border-t border-white/10 pt-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-bold text-white">Video Background</h4>
            <p className="text-xs text-gray-500">Play a looping video behind the hero text</p>
          </div>
          <button
            onClick={() => handleChange("video_enabled", !content.video_enabled)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${content.video_enabled
              ? 'bg-accent text-black'
              : 'bg-white/10 text-gray-500'
              }`}
          >
            {content.video_enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>

        <InputGroup label="Video URL" description="Direct link to an MP4 video file">
          <input
            value={content.video_url || ""}
            onChange={(e) => handleChange("video_url", e.target.value)}
            disabled={!content.video_enabled}
            className={`w-full bg-black border border-white/20 p-3 rounded-lg text-sm font-mono focus:border-accent outline-none ${content.video_enabled ? 'text-white' : 'text-gray-600 opacity-50'
              }`}
            placeholder="https://example.com/video.mp4"
          />
        </InputGroup>

        {/* Sound Toggle */}
        <div className="flex items-center justify-between mt-4 p-4 bg-neutral-900/50 rounded-lg border border-white/5">
          <div>
            <h5 className="font-bold text-white text-sm">Video Sound</h5>
            <p className="text-xs text-gray-500">Allow visitors to unmute the video with a button</p>
          </div>
          <button
            onClick={() => handleChange("sound_enabled", !content.sound_enabled)}
            disabled={!content.video_enabled}
            className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${content.sound_enabled && content.video_enabled
                ? 'bg-accent text-black'
                : 'bg-white/10 text-gray-500'
              } ${!content.video_enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {content.sound_enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderAboutEditor = () => (
    <div className="space-y-8 max-w-3xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputGroup label="Headline Part 1">
          <input
            value={content.headline_1 || ""}
            onChange={(e) => handleChange("headline_1", e.target.value)}
            className="w-full bg-black border border-white/20 p-3 rounded-lg text-white focus:border-accent outline-none font-bold"
          />
        </InputGroup>
        <InputGroup label="Headline Part 2 (Colored)">
          <input
            value={content.headline_2 || ""}
            onChange={(e) => handleChange("headline_2", e.target.value)}
            className="w-full bg-black border border-white/20 p-3 rounded-lg text-accent focus:border-accent outline-none font-bold"
          />
        </InputGroup>
      </div>

      <InputGroup label="Main Description">
        <textarea
          value={content.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          rows="5"
          className="w-full bg-black border border-white/20 p-4 rounded-lg text-white focus:border-accent outline-none leading-relaxed"
        />
      </InputGroup>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Key Statistics</h3>
          <button
            onClick={() => addListItem("stats", { label: "New Stat", value: "00" })}
            className="bg-white/10 hover:bg-accent hover:text-black text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Stat
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(content.stats || []).map((stat, i) => (
            <div key={i} className="bg-neutral-900 p-4 rounded-xl border border-white/10 relative group">
              <button
                onClick={() => removeListItem("stats", i)}
                className="absolute top-2 right-2 text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Number</label>
              <input
                value={stat.value}
                onChange={(e) => updateListItem("stats", i, "value", e.target.value)}
                className="w-full bg-black border border-white/10 p-2 rounded text-2xl font-mono text-white mb-3 focus:border-accent outline-none"
              />
              <label className="text-xs text-gray-500 uppercase font-bold block mb-1">Label</label>
              <input
                value={stat.label}
                onChange={(e) => updateListItem("stats", i, "label", e.target.value)}
                className="w-full bg-black border border-white/10 p-2 rounded text-sm text-gray-300 focus:border-accent outline-none"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderServicesEditor = () => (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-accent/10 border border-accent/20 p-4 rounded-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          {content.showImages !== false ? <Eye className="w-5 h-5 text-accent" /> : <EyeOff className="w-5 h-5 text-gray-500" />}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider">Service Images</h4>
            <p className="text-gray-400 text-xs">Toggle photos on/off in the list below</p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={content.showImages !== false}
            onChange={(e) => handleChange("showImages", e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
        </label>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-400 text-sm">Manage the services listed on your homepage.</p>
        <button
          onClick={() => addListItem("services", { title: "New Service", desc: "Short Desc", details: "Full details...", src: "", hasTech: false })}
          className="bg-accent text-black px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Service
        </button>
      </div>

      <div className="space-y-4">
        {(content.services || []).map((service, i) => {
          const isExpanded = expandedItems[`services-${i}`];
          return (
            <div key={i} className="bg-neutral-900 border border-white/10 rounded-xl overflow-hidden transition-all duration-300">
              {/* Header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer bg-white/5 hover:bg-white/10"
                onClick={() => toggleExpand(`services-${i}`)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center border border-white/10 text-xs font-mono text-gray-500">
                    {i + 1}
                  </div>
                  <h4 className="font-bold text-white text-lg">{service.title || "Untitled Service"}</h4>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); removeListItem("services", i); }}
                    className="p-2 hover:bg-red-500/20 text-gray-500 hover:text-red-500 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                </div>
              </div>

              {/* Body */}
              {isExpanded && (
                <div className="p-6 border-t border-white/10 space-y-6 bg-black/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase">Service Name</label>
                      <input
                        value={service.title}
                        onChange={(e) => updateListItem("services", i, "title", e.target.value)}
                        className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase">Short Tagline</label>
                      <input
                        value={service.desc}
                        onChange={(e) => updateListItem("services", i, "desc", e.target.value)}
                        className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Detailed Description</label>
                    <textarea
                      value={service.details}
                      onChange={(e) => updateListItem("services", i, "details", e.target.value)}
                      rows="3"
                      className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2">
                      <ImageIcon className="w-3 h-3" /> Image URL
                    </label>
                    <input
                      value={service.src}
                      onChange={(e) => updateListItem("services", i, "src", e.target.value)}
                      className="w-full bg-black border border-white/10 p-3 rounded-lg text-accent text-sm font-mono focus:border-accent outline-none"
                      placeholder="https://..."
                    />
                    {service.src && (
                      <div className="h-32 w-full rounded-lg overflow-hidden border border-white/10 mt-2">
                        <img src={service.src} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={service.hasTech}
                      onChange={(e) => updateListItem("services", i, "hasTech", e.target.checked)}
                      className="w-5 h-5 rounded border-white/20 bg-black checked:bg-accent"
                    />
                    <label className="text-sm text-white">Show Coding Languages (Tech Stack) for this service?</label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderProcessEditor = () => (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-400 text-sm">Define the steps of your workflow.</p>
        <button
          onClick={() => addListItem("steps", { num: "0" + ((content.steps?.length || 0) + 1), title: "New Step", desc: "Step description..." })}
          className="bg-accent text-black px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Step
        </button>
      </div>

      <div className="space-y-4">
        {(content.steps || []).map((step, i) => (
          <div key={i} className="bg-neutral-900 border border-white/10 rounded-xl p-6 relative group hover:border-white/20 transition-colors">
            <button
              onClick={() => removeListItem("steps", i)}
              className="absolute top-4 right-4 text-gray-600 hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
            </button>

            <div className="flex gap-4">
              <div className="w-16">
                <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Step #</label>
                <input
                  value={step.num}
                  onChange={(e) => updateListItem("steps", i, "num", e.target.value)}
                  className="w-full bg-black border border-white/10 p-3 rounded-lg text-accent font-mono font-bold text-center focus:border-accent outline-none"
                />
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Step Title</label>
                  <input
                    value={step.title}
                    onChange={(e) => updateListItem("steps", i, "title", e.target.value)}
                    className="w-full bg-black border border-white/10 p-3 rounded-lg text-white font-bold focus:border-accent outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-2">Description</label>
                  <textarea
                    value={step.desc}
                    onChange={(e) => updateListItem("steps", i, "desc", e.target.value)}
                    rows="2"
                    className="w-full bg-black border border-white/10 p-3 rounded-lg text-gray-300 text-sm focus:border-accent outline-none resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderQuoteRequestsEditor = () => {
    const filteredQuotes = quotes.filter(quote => {
      if (!quoteSearch.trim()) return true;
      const searchLower = quoteSearch.toLowerCase();
      return (
        quote.name?.toLowerCase().includes(searchLower) ||
        quote.email?.toLowerCase().includes(searchLower) ||
        quote.company?.toLowerCase().includes(searchLower) ||
        quote.project_type?.toLowerCase().includes(searchLower) ||
        quote.budget?.toLowerCase().includes(searchLower) ||
        quote.details?.toLowerCase().includes(searchLower)
      );
    });

    return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-400 text-sm">All quote requests from potential clients.</p>
        <span className="bg-accent/20 text-accent px-3 py-1 rounded-full text-xs font-bold">
          {quotes.length} {quotes.length === 1 ? 'Request' : 'Requests'}
        </span>
      </div>

      {/* Search Bar */}
      {quotes.length > 0 && (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={quoteSearch}
            onChange={(e) => setQuoteSearch(e.target.value)}
            placeholder="Search by name, email, company, project type..."
            className="w-full bg-black border border-white/10 pl-12 pr-4 py-3 rounded-xl text-white placeholder-gray-500 focus:border-accent outline-none transition-colors"
          />
          {quoteSearch && (
            <button
              onClick={() => setQuoteSearch("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {quotes.length === 0 ? (
        <div className="bg-neutral-900 border border-white/10 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h4 className="text-white font-bold text-lg mb-2">No Quote Requests Yet</h4>
          <p className="text-gray-500 text-sm">When someone submits a quote request, it will appear here.</p>
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="bg-neutral-900 border border-white/10 rounded-xl p-12 text-center">
          <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h4 className="text-white font-bold text-lg mb-2">No Results Found</h4>
          <p className="text-gray-500 text-sm">No quotes match "{quoteSearch}". Try a different search term.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quoteSearch && (
            <p className="text-gray-500 text-sm">
              Showing {filteredQuotes.length} of {quotes.length} requests
            </p>
          )}
          {filteredQuotes.map((quote, i) => {
            const isExpanded = expandedItems[`quote-${i}`];
            return (
              <div key={quote.id || i} className="bg-neutral-900 border border-white/10 rounded-xl overflow-hidden">
                <div
                  className="p-4 flex items-center justify-between cursor-pointer bg-white/5 hover:bg-white/10"
                  onClick={() => toggleExpand(`quote-${i}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{quote.name}</h4>
                      <p className="text-gray-500 text-xs">{quote.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="bg-white/10 text-gray-300 px-3 py-1 rounded-full text-xs hidden md:block">
                      {quote.project_type}
                    </span>
                    <span className="text-accent font-mono text-sm hidden md:block">{quote.budget}</span>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-6 border-t border-white/10 bg-black/20 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-3">
                        <Building className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Company</p>
                          <p className="text-white text-sm">{quote.company || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Budget</p>
                          <p className="text-accent text-sm font-mono">{quote.budget}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Submitted</p>
                          <p className="text-white text-sm">{new Date(quote.timestamp).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-2">Project Details</p>
                      <p className="text-gray-300 text-sm bg-black/50 p-4 rounded-lg border border-white/5">{quote.details}</p>
                    </div>
                    {/* Delete button - Superadmin only */}
                    {adminRole === "superadmin" && (
                      <div className="pt-4 border-t border-white/10 flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteQuote(quote.id);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Request
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
  };

  const renderContactsEditor = () => {
    const filteredContacts = contacts.filter((contact) => {
      if (!contactSearch.trim()) return true;
      const searchLower = contactSearch.toLowerCase();
      return (
        contact.name?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.message?.toLowerCase().includes(searchLower)
      );
    });

    return (
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-400 text-sm">Messages submitted through the public contact form.</p>
          <span className="bg-accent/20 text-accent px-3 py-1 rounded-full text-xs font-bold">
            {contacts.length} {contacts.length === 1 ? "Message" : "Messages"}
          </span>
        </div>

        {contacts.length > 0 && (
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search by name, email, or message..."
              className="w-full bg-black border border-white/10 pl-12 pr-4 py-3 rounded-xl text-white placeholder-gray-500 focus:border-accent outline-none transition-colors"
            />
          </div>
        )}

        {contacts.length === 0 ? (
          <div className="bg-neutral-900 border border-white/10 rounded-xl p-12 text-center">
            <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h4 className="text-white font-bold text-lg mb-2">No Messages Yet</h4>
            <p className="text-gray-500 text-sm">When someone uses the contact form, it will appear here.</p>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="bg-neutral-900 border border-white/10 rounded-xl p-12 text-center">
            <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h4 className="text-white font-bold text-lg mb-2">No Results Found</h4>
            <p className="text-gray-500 text-sm">No messages match that search.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredContacts.map((contact, i) => {
              const isExpanded = expandedItems[`contact-${i}`];
              return (
                <div key={contact.id || i} className="bg-neutral-900 border border-white/10 rounded-xl overflow-hidden">
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer bg-white/5 hover:bg-white/10"
                    onClick={() => toggleExpand(`contact-${i}`)}
                  >
                    <div>
                      <h4 className="font-bold text-white">{contact.name}</h4>
                      <p className="text-gray-500 text-xs">{contact.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-400 text-xs hidden md:block">
                        {contact.timestamp ? new Date(contact.timestamp).toLocaleDateString() : ""}
                      </span>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="p-6 border-t border-white/10 bg-black/20 space-y-4">
                      <p className="text-gray-300 text-sm bg-black/50 p-4 rounded-lg border border-white/5 whitespace-pre-wrap">
                        {contact.message}
                      </p>
                      <div className="flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteContact(contact.id);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Message
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderQuoteSettingsEditor = () => (
    <div className="space-y-6 max-w-2xl">
      <InputGroup label="Form Title" description="The headline shown on the quote popup">
        <input
          value={content.title || ""}
          onChange={(e) => handleChange("title", e.target.value)}
          className="w-full bg-black border border-white/20 p-4 rounded-lg text-white focus:border-accent outline-none"
          placeholder="Let's start with the basics."
        />
      </InputGroup>

      <InputGroup label="Success Message" description="Shown after form submission">
        <input
          value={content.success_message || ""}
          onChange={(e) => handleChange("success_message", e.target.value)}
          className="w-full bg-black border border-white/20 p-4 rounded-lg text-white focus:border-accent outline-none"
          placeholder="We'll get back to you within 24 hours."
        />
      </InputGroup>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Project Types</h3>
          <button
            onClick={() => addListItem("project_types", "New Project Type")}
            className="bg-white/10 hover:bg-accent hover:text-black text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Type
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(content.project_types || []).map((type, i) => (
            <div key={i} className="bg-neutral-900 p-3 rounded-lg border border-white/10 flex items-center gap-3 group">
              <input
                value={type}
                onChange={(e) => {
                  const newTypes = [...(content.project_types || [])];
                  newTypes[i] = e.target.value;
                  setContent({ ...content, project_types: newTypes });
                }}
                className="flex-1 bg-black border border-white/10 p-2 rounded text-white text-sm focus:border-accent outline-none"
              />
              <button
                onClick={() => {
                  const newTypes = [...(content.project_types || [])].filter((_, idx) => idx !== i);
                  setContent({ ...content, project_types: newTypes });
                }}
                className="text-gray-600 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Budget Ranges</h3>
            <p className="text-xs text-gray-500 mt-1">These options appear in the budget selection step</p>
          </div>
          <button
            onClick={() => addListItem("budget_ranges", "New Range")}
            className="bg-white/10 hover:bg-accent hover:text-black text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Range
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(content.budget_ranges || []).map((range, i) => (
            <div key={i} className="bg-neutral-900 p-3 rounded-lg border border-white/10 flex items-center gap-3 group">
              <DollarSign className="w-4 h-4 text-accent" />
              <input
                value={range}
                onChange={(e) => {
                  const newRanges = [...(content.budget_ranges || [])];
                  newRanges[i] = e.target.value;
                  setContent({ ...content, budget_ranges: newRanges });
                }}
                className="flex-1 bg-black border border-white/10 p-2 rounded text-white text-sm focus:border-accent outline-none"
              />
              <button
                onClick={() => {
                  const newRanges = [...(content.budget_ranges || [])].filter((_, idx) => idx !== i);
                  setContent({ ...content, budget_ranges: newRanges });
                }}
                className="text-gray-600 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {(!content.budget_ranges || content.budget_ranges.length === 0) && (
          <p className="text-gray-500 text-sm text-center py-4 bg-neutral-900/50 rounded-lg border border-dashed border-white/10">
            No custom budget ranges. Using default ranges based on user's location.
          </p>
        )}
      </div>
    </div>
  );

  const renderTrustedByEditor = () => (
    <div className="space-y-6 max-w-2xl">
      {/* Enable/Disable Toggle */}
      <div className="bg-accent/10 border border-accent/20 p-4 rounded-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          {content.enabled !== false ? <Eye className="w-5 h-5 text-accent" /> : <EyeOff className="w-5 h-5 text-gray-500" />}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider">Show Section</h4>
            <p className="text-gray-400 text-xs">Toggle the Trusted By section on/off</p>
          </div>
        </div>
        <button
          onClick={() => handleChange("enabled", !content.enabled)}
          className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${content.enabled !== false
            ? 'bg-accent text-black'
            : 'bg-white/10 text-gray-500'
            }`}
        >
          {content.enabled !== false ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <InputGroup label="Section Title" description="The heading above the client logos">
        <input
          value={content.title || ""}
          onChange={(e) => handleChange("title", e.target.value)}
          className="w-full bg-black border border-white/20 p-4 rounded-lg text-white focus:border-accent outline-none"
          placeholder="Trusted by Industry Leaders"
        />
      </InputGroup>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Client Names</h3>
            <p className="text-gray-500 text-xs">These will scroll horizontally in the marquee</p>
          </div>
          <button
            onClick={() => {
              const newClients = [...(content.clients || []), "New Client"];
              handleChange("clients", newClients);
            }}
            className="bg-accent text-black px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Client
          </button>
        </div>

        <div className="space-y-3">
          {(content.clients || []).map((client, index) => (
            <div key={index} className="flex gap-3 items-center">
              <span className="text-gray-600 font-mono text-sm w-8">{index + 1}.</span>
              <input
                type="text"
                value={client}
                onChange={(e) => {
                  const newClients = [...(content.clients || [])];
                  newClients[index] = e.target.value;
                  handleChange("clients", newClients);
                }}
                className="flex-1 bg-black p-3 rounded-lg border border-white/10 focus:border-accent focus:outline-none text-white font-display font-bold uppercase"
                placeholder="Client name..."
              />
              <button
                onClick={() => {
                  const newClients = (content.clients || []).filter((_, i) => i !== index);
                  handleChange("clients", newClients);
                }}
                className="p-3 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-transparent hover:border-red-500/20"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        {(content.clients || []).length === 0 && (
          <div className="bg-neutral-900 border border-white/10 rounded-xl p-8 text-center">
            <Award className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No clients added yet. Click "Add Client" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderTickerEditor = () => (
    <div className="space-y-8">
      <div className="border-b border-white/10 pb-6">
        <h3 className="text-2xl font-bold mb-2">Scrolling Banner Text</h3>
        <p className="text-gray-400">Add phrases that highlight your company's values and strengths.</p>
      </div>

      <InputGroup label="Scroll Speed" description="Control how fast the text scrolls (lower = faster, higher = slower). Remember to click Save after adjusting.">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="5"
              max="200"
              step="5"
              value={content.speed || 20}
              onChange={(e) => handleChange("speed", parseInt(e.target.value))}
              className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="bg-black border border-white/10 px-4 py-2 rounded-lg min-w-[90px] text-center">
              <span className="text-accent font-mono font-bold text-lg">{content.speed || 20}s</span>
            </div>
            <button
              onClick={() => handleChange("speed", 20)}
              className="px-3 py-2 bg-white/10 hover:bg-accent hover:text-black text-white rounded-lg text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
              title="Reset to default speed"
            >
              Reset
            </button>
          </div>
          <div className="flex justify-between text-xs text-gray-500 px-1">
            <span>Fast (5s)</span>
            <span>Default (20s)</span>
            <span>Very Slow (200s)</span>
          </div>
          {(content.speed || 20) !== 20 && (
            <div className="bg-accent/10 border border-accent/20 p-3 rounded-lg">
              <p className="text-accent text-xs font-bold">⚠ Don't forget to click the Save button at the top to apply changes!</p>
            </div>
          )}
        </div>
      </InputGroup>

      <InputGroup label="Scrolling Phrases" description="These will scroll horizontally on the homepage.">
        <div className="space-y-3">
          {(content.texts || []).map((text, index) => (
            <div key={index} className="flex gap-3">
              <input
                type="text"
                value={text}
                onChange={(e) => {
                  const newTexts = [...(content.texts || [])];
                  newTexts[index] = e.target.value;
                  setContent({ ...content, texts: newTexts });
                }}
                className="flex-1 bg-black p-3 rounded-lg border border-white/10 focus:border-accent focus:outline-none text-white font-mono text-sm"
                placeholder="Enter phrase..."
              />
              <button
                onClick={() => {
                  const newTexts = (content.texts || []).filter((_, i) => i !== index);
                  setContent({ ...content, texts: newTexts });
                }}
                className="p-3 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-transparent hover:border-red-500/20"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              setContent({ ...content, texts: [...(content.texts || []), ""] });
            }}
            className="flex items-center gap-2 text-accent text-sm font-bold uppercase tracking-widest hover:text-white transition-colors py-2"
          >
            <Plus className="w-4 h-4" /> Add Phrase
          </button>
        </div>
      </InputGroup>
    </div>
  );

  const renderFooterEditor = () => (
    <div className="space-y-8 max-w-3xl">
      <div className="border-b border-white/10 pb-6">
        <h3 className="text-2xl font-bold mb-2">Footer Settings</h3>
        <p className="text-gray-400">Customize the copyright year, company name, and social media links.</p>
      </div>

      <InputGroup label="Company Name" description="The name displayed in the footer copyright">
        <input
          type="text"
          value={content.company_name || "XLUMA.IN"}
          onChange={(e) => handleChange("company_name", e.target.value)}
          className="w-full bg-black border border-white/20 p-3 rounded-lg text-white focus:border-accent outline-none font-mono"
          placeholder="XLUMA.IN"
        />
      </InputGroup>

      <InputGroup label="Copyright Year" description="The year displayed in the footer copyright notice">
        <div className="flex items-center gap-4">
          <input
            type="number"
            min="2020"
            max="2100"
            value={content.year || new Date().getFullYear()}
            onChange={(e) => handleChange("year", parseInt(e.target.value))}
            className="w-32 bg-black border border-white/20 p-3 rounded-lg text-xl text-white focus:border-accent outline-none font-mono text-center"
          />
          <button
            onClick={() => handleChange("year", new Date().getFullYear())}
            className="px-4 py-2 bg-white/10 hover:bg-accent hover:text-black text-white rounded-lg text-xs font-bold uppercase tracking-widest transition-colors"
          >
            Use Current Year
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Preview: © {content.year || new Date().getFullYear()} {content.company_name || "XLUMA.IN"}</p>
      </InputGroup>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Social Media Links</h3>
            <p className="text-xs text-gray-500 mt-1">Add or edit your social media links</p>
          </div>
          <button
            onClick={() => {
              const newLinks = [...(content.social_links || []), { name: "New Link", url: "#" }];
              handleChange("social_links", newLinks);
            }}
            className="bg-white/10 hover:bg-accent hover:text-black text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Link
          </button>
        </div>

        <div className="space-y-3">
          {(content.social_links || []).map((link, index) => (
            <div key={index} className="bg-neutral-900/50 p-4 rounded-xl border border-white/5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Link Name</label>
                  <input
                    type="text"
                    value={link.name}
                    onChange={(e) => {
                      const newLinks = [...(content.social_links || [])];
                      newLinks[index] = { ...newLinks[index], name: e.target.value };
                      handleChange("social_links", newLinks);
                    }}
                    className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                    placeholder="Instagram"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">URL</label>
                  <input
                    type="text"
                    value={link.url}
                    onChange={(e) => {
                      const newLinks = [...(content.social_links || [])];
                      newLinks[index] = { ...newLinks[index], url: e.target.value };
                      handleChange("social_links", newLinks);
                    }}
                    className="w-full bg-black border border-white/10 p-3 rounded-lg text-accent text-sm font-mono focus:border-accent outline-none"
                    placeholder="https://instagram.com/yourprofile"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  const newLinks = (content.social_links || []).filter((_, i) => i !== index);
                  handleChange("social_links", newLinks);
                }}
                className="mt-3 text-red-400 hover:text-red-500 text-xs font-bold uppercase tracking-widest flex items-center gap-2 transition-colors"
              >
                <Trash2 className="w-3 h-3" /> Remove Link
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderProjectsEditor = () => (
    <div className="space-y-8">
      <div className="border-b border-white/10 pb-6 flex justify-between items-start">
        <div>
          <h3 className="text-2xl font-bold mb-2">Projects Portfolio</h3>
          <p className="text-gray-400">Manage your project case studies.</p>
        </div>
        <button
          onClick={() => {
            const newProject = {
              id: `temp-${Date.now()}`,
              title: "New Project",
              category: "Category",
              image_url: "",
              tags: [],
              featured: false,
              order: projects.length + 1,
              redirect_url: "",
              redirect_enabled: false
            };
            setProjects([...projects, newProject]);
          }}
          className="flex items-center gap-2 bg-accent text-black px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-widest hover:bg-white transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Project
        </button>
      </div>

      <div className="space-y-6">
        {projects.map((project, index) => (
          <div key={project.id} className="bg-neutral-900/50 p-6 rounded-xl border border-white/5 space-y-4">
            <div className="flex justify-between items-start">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 mr-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Project Title</label>
                  <input
                    type="text"
                    value={project.title}
                    onChange={(e) => {
                      const newProjects = [...projects];
                      newProjects[index] = { ...project, title: e.target.value };
                      setProjects(newProjects);
                    }}
                    className="w-full bg-black p-2 rounded border border-white/10 text-white font-bold"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Category</label>
                  <input
                    type="text"
                    value={project.category}
                    onChange={(e) => {
                      const newProjects = [...projects];
                      newProjects[index] = { ...project, category: e.target.value };
                      setProjects(newProjects);
                    }}
                    className="w-full bg-black p-2 rounded border border-white/10 text-sm font-mono text-gray-400"
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  if (confirm(`Delete "${project.title}"?`)) {
                    setProjects(projects.filter((_, i) => i !== index));
                  }
                }}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-transparent hover:border-red-500/20"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Cover Image URL</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={project.image_url}
                  onChange={(e) => {
                    const newProjects = [...projects];
                    newProjects[index] = { ...project, image_url: e.target.value };
                    setProjects(newProjects);
                  }}
                  className="flex-1 bg-black p-2 rounded border border-white/10 text-sm font-mono text-white"
                  placeholder="https://..."
                />
                {project.image_url && (
                  <div className="w-10 h-10 rounded overflow-hidden border border-white/20 shrink-0">
                    <img src={project.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-white/5 pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Redirect URL (click to open)</label>
                <button
                  onClick={() => {
                    const newProjects = [...projects];
                    newProjects[index] = { ...project, redirect_enabled: !project.redirect_enabled };
                    setProjects(newProjects);
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${project.redirect_enabled
                    ? 'bg-accent text-black'
                    : 'bg-white/10 text-gray-500'
                    }`}
                >
                  {project.redirect_enabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>
              <input
                type="text"
                value={project.redirect_url || ""}
                onChange={(e) => {
                  const newProjects = [...projects];
                  newProjects[index] = { ...project, redirect_url: e.target.value };
                  setProjects(newProjects);
                }}
                disabled={!project.redirect_enabled}
                className={`w-full bg-black p-2 rounded border border-white/10 text-sm font-mono ${project.redirect_enabled ? 'text-white' : 'text-gray-600 opacity-50'
                  }`}
                placeholder="https://example.com/project-page"
              />
            </div>

            <div className="border-t border-white/5 pt-4">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-3">Technologies / Languages</label>

              {/* Current Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {(project.tags || []).map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    className="inline-flex items-center gap-1 bg-accent/20 text-accent px-2 py-1 rounded text-xs font-mono uppercase"
                  >
                    {tag}
                    <button
                      onClick={() => {
                        const newProjects = [...projects];
                        newProjects[index] = {
                          ...project,
                          tags: project.tags.filter((_, i) => i !== tagIndex)
                        };
                        setProjects(newProjects);
                      }}
                      className="ml-1 hover:text-red-400 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {(!project.tags || project.tags.length === 0) && (
                  <span className="text-gray-600 text-xs">No tags added</span>
                )}
              </div>

              {/* Add Tag Dropdown */}
              <select
                className="bg-black p-2 rounded border border-white/10 text-sm text-white w-full md:w-auto"
                value=""
                onChange={(e) => {
                  if (e.target.value && !(project.tags || []).includes(e.target.value)) {
                    const newProjects = [...projects];
                    newProjects[index] = {
                      ...project,
                      tags: [...(project.tags || []), e.target.value]
                    };
                    setProjects(newProjects);
                  }
                  e.target.value = "";
                }}
              >
                <option value="">+ Add Technology...</option>
                <optgroup label="Frontend">
                  <option value="React">React</option>
                  <option value="Vue">Vue</option>
                  <option value="Angular">Angular</option>
                  <option value="Next.js">Next.js</option>
                  <option value="Svelte">Svelte</option>
                  <option value="TypeScript">TypeScript</option>
                  <option value="JavaScript">JavaScript</option>
                  <option value="HTML/CSS">HTML/CSS</option>
                  <option value="Tailwind CSS">Tailwind CSS</option>
                </optgroup>
                <optgroup label="Backend">
                  <option value="Node">Node</option>
                  <option value="Python">Python</option>
                  <option value="Go">Go</option>
                  <option value="Rust">Rust</option>
                  <option value="Java">Java</option>
                  <option value="PHP">PHP</option>
                  <option value="Ruby">Ruby</option>
                  <option value="C#">C#</option>
                </optgroup>
                <optgroup label="Database">
                  <option value="MongoDB">MongoDB</option>
                  <option value="PostgreSQL">PostgreSQL</option>
                  <option value="MySQL">MySQL</option>
                  <option value="Firebase">Firebase</option>
                  <option value="Redis">Redis</option>
                  <option value="GraphQL">GraphQL</option>
                </optgroup>
                <optgroup label="DevOps / Cloud">
                  <option value="AWS">AWS</option>
                  <option value="Docker">Docker</option>
                  <option value="Kubernetes">Kubernetes</option>
                  <option value="Vercel">Vercel</option>
                  <option value="GCP">GCP</option>
                  <option value="Azure">Azure</option>
                </optgroup>
                <optgroup label="Web3 / 3D">
                  <option value="Solidity">Solidity</option>
                  <option value="WebGL">WebGL</option>
                  <option value="Three.js">Three.js</option>
                  <option value="GSAP">GSAP</option>
                  <option value="D3.js">D3.js</option>
                </optgroup>
                <optgroup label="Mobile">
                  <option value="React Native">React Native</option>
                  <option value="Flutter">Flutter</option>
                  <option value="Swift">Swift</option>
                  <option value="Kotlin">Kotlin</option>
                </optgroup>
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Users management state for new user form
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState("admin");
  const [addingUser, setAddingUser] = useState(false);

  const handleAddUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      toast.error("Email and password are required");
      return;
    }
    setAddingUser(true);
    try {
      const token = localStorage.getItem("admin_token");
      await api.post(`/admins`, {
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User created successfully!");
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserRole("admin");
      fetchContent("users");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!confirm(`Delete user "${userEmail}"?`)) return;
    try {
      const token = localStorage.getItem("admin_token");
      await api.delete(`/admins/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted");
      fetchContent("users");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const renderBlogsEditor = () => {
    if (editingBlog !== null) {
      const blog = blogs[editingBlog];
      return (
        <div className="space-y-6 max-w-3xl">
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <h3 className="text-xl font-bold">{blog.id.startsWith('temp-') ? 'New Blog Post' : 'Edit Blog Post'}</h3>
            <button
              onClick={() => setEditingBlog(null)}
              className="text-gray-500 hover:text-white text-sm"
            >
              ← Back to list
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Title</label>
              <input
                value={blog.title}
                onChange={(e) => {
                  const newBlogs = [...blogs];
                  newBlogs[editingBlog] = { ...blog, title: e.target.value };
                  setBlogs(newBlogs);
                }}
                className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                placeholder="Blog title..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Slug (URL)</label>
              <div className="flex gap-2">
                <input
                  value={blog.slug}
                  onChange={(e) => {
                    const newBlogs = [...blogs];
                    newBlogs[editingBlog] = { ...blog, slug: e.target.value };
                    setBlogs(newBlogs);
                  }}
                  className="flex-1 bg-black border border-white/10 p-3 rounded-lg text-white font-mono text-sm focus:border-accent outline-none"
                  placeholder="blog-url-slug"
                />
                <button
                  onClick={() => {
                    const newBlogs = [...blogs];
                    newBlogs[editingBlog] = { ...blog, slug: generateSlug(blog.title) };
                    setBlogs(newBlogs);
                  }}
                  className="px-3 bg-white/10 rounded-lg text-xs hover:bg-white/20 transition-colors"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Category</label>
              <input
                value={blog.category || ''}
                onChange={(e) => {
                  const newBlogs = [...blogs];
                  newBlogs[editingBlog] = { ...blog, category: e.target.value };
                  setBlogs(newBlogs);
                }}
                className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                placeholder="e.g., Design, Development"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Author</label>
              <input
                value={blog.author || ''}
                onChange={(e) => {
                  const newBlogs = [...blogs];
                  newBlogs[editingBlog] = { ...blog, author: e.target.value };
                  setBlogs(newBlogs);
                }}
                className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                placeholder="Author name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Read Time (min)</label>
              <input
                type="number"
                value={blog.read_time || ''}
                onChange={(e) => {
                  const newBlogs = [...blogs];
                  newBlogs[editingBlog] = { ...blog, read_time: parseInt(e.target.value) || null };
                  setBlogs(newBlogs);
                }}
                className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
                placeholder="5"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase">Cover Image URL</label>
            <input
              value={blog.cover_image || ''}
              onChange={(e) => {
                const newBlogs = [...blogs];
                newBlogs[editingBlog] = { ...blog, cover_image: e.target.value };
                setBlogs(newBlogs);
              }}
              className="w-full bg-black border border-white/10 p-3 rounded-lg text-accent font-mono text-sm focus:border-accent outline-none"
              placeholder="https://..."
            />
            {blog.cover_image && (
              <div className="h-40 rounded-lg overflow-hidden border border-white/10 mt-2">
                <img src={blog.cover_image} alt="Cover" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase">Excerpt (Short Description)</label>
            <textarea
              value={blog.excerpt || ''}
              onChange={(e) => {
                const newBlogs = [...blogs];
                newBlogs[editingBlog] = { ...blog, excerpt: e.target.value };
                setBlogs(newBlogs);
              }}
              rows="2"
              className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none resize-none"
              placeholder="A brief summary of the blog post..."
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-500 uppercase">Content Format</label>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const newBlogs = [...blogs];
                    newBlogs[editingBlog] = { ...blog, content_format: 'markdown' };
                    setBlogs(newBlogs);
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${
                    (blog.content_format || 'markdown') === 'markdown'
                      ? 'bg-accent text-black'
                      : 'bg-white/10 text-gray-500 hover:bg-white/20'
                  }`}
                >
                  <FileCode className="w-3 h-3" />
                  Markdown Builder
                </button>
                <button
                  onClick={() => {
                    const newBlogs = [...blogs];
                    newBlogs[editingBlog] = { ...blog, content_format: 'html' };
                    setBlogs(newBlogs);
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1 transition-colors ${
                    blog.content_format === 'html'
                      ? 'bg-accent text-black'
                      : 'bg-white/10 text-gray-500 hover:bg-white/20'
                  }`}
                >
                  <Code className="w-3 h-3" />
                  HTML
                </button>
              </div>
            </div>

            {(blog.content_format || 'markdown') === 'markdown' ? (
              // Structured Markdown Builder
              <div className="space-y-4 bg-neutral-900/50 p-6 rounded-xl border border-white/10">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Main Title (H1)</label>
                  <input
                    value={blog.markdown_title || ''}
                    onChange={(e) => {
                      const newBlogs = [...blogs];
                      newBlogs[editingBlog] = { ...blog, markdown_title: e.target.value };
                      setBlogs(newBlogs);
                    }}
                    className="w-full bg-black border border-white/10 p-3 rounded-lg text-white text-xl font-bold focus:border-accent outline-none"
                    placeholder="Your Main Blog Title"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Introduction Paragraph</label>
                  <textarea
                    value={blog.markdown_intro || ''}
                    onChange={(e) => {
                      const newBlogs = [...blogs];
                      newBlogs[editingBlog] = { ...blog, markdown_intro: e.target.value };
                      setBlogs(newBlogs);
                    }}
                    rows="3"
                    className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none resize-none"
                    placeholder="Write your introduction paragraph here..."
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-500 uppercase">Content Sections</label>
                    <button
                      onClick={() => {
                        const newBlogs = [...blogs];
                        const sections = blog.markdown_sections || [];
                        newBlogs[editingBlog] = {
                          ...blog,
                          markdown_sections: [...sections, { type: 'heading', content: '' }]
                        };
                        setBlogs(newBlogs);
                      }}
                      className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1 rounded-lg flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Section
                    </button>
                  </div>

                  {(blog.markdown_sections || []).map((section, idx) => (
                    <div key={idx} className="bg-black/50 p-4 rounded-lg border border-white/5 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <select
                          value={section.type}
                          onChange={(e) => {
                            const newBlogs = [...blogs];
                            const sections = [...(blog.markdown_sections || [])];
                            sections[idx] = { ...section, type: e.target.value };
                            newBlogs[editingBlog] = { ...blog, markdown_sections: sections };
                            setBlogs(newBlogs);
                          }}
                          className="bg-neutral-900 border border-white/10 px-3 py-2 rounded-lg text-white text-xs focus:border-accent outline-none"
                        >
                          <option value="heading">Heading (H2)</option>
                          <option value="subheading">Subheading (H3)</option>
                          <option value="paragraph">Paragraph</option>
                          <option value="list">Bullet List</option>
                          <option value="numbered">Numbered List</option>
                          <option value="quote">Quote</option>
                          <option value="code">Code Block</option>
                        </select>
                        <button
                          onClick={() => {
                            const newBlogs = [...blogs];
                            const sections = (blog.markdown_sections || []).filter((_, i) => i !== idx);
                            newBlogs[editingBlog] = { ...blog, markdown_sections: sections };
                            setBlogs(newBlogs);
                          }}
                          className="text-red-500 hover:text-red-400 p-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {section.type === 'list' || section.type === 'numbered' ? (
                        <div className="space-y-2">
                          {(section.items || ['']).map((item, itemIdx) => (
                            <div key={itemIdx} className="flex gap-2">
                              <input
                                value={item}
                                onChange={(e) => {
                                  const newBlogs = [...blogs];
                                  const sections = [...(blog.markdown_sections || [])];
                                  const items = [...(sections[idx].items || [''])];
                                  items[itemIdx] = e.target.value;
                                  sections[idx] = { ...section, items };
                                  newBlogs[editingBlog] = { ...blog, markdown_sections: sections };
                                  setBlogs(newBlogs);
                                }}
                                className="flex-1 bg-neutral-900 border border-white/10 p-2 rounded-lg text-white text-sm focus:border-accent outline-none"
                                placeholder={`List item ${itemIdx + 1}`}
                              />
                              {itemIdx === (section.items || ['']).length - 1 && (
                                <button
                                  onClick={() => {
                                    const newBlogs = [...blogs];
                                    const sections = [...(blog.markdown_sections || [])];
                                    const items = [...(sections[idx].items || ['']), ''];
                                    sections[idx] = { ...section, items };
                                    newBlogs[editingBlog] = { ...blog, markdown_sections: sections };
                                    setBlogs(newBlogs);
                                  }}
                                  className="bg-white/10 hover:bg-white/20 px-3 rounded-lg"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <textarea
                          value={section.content || ''}
                          onChange={(e) => {
                            const newBlogs = [...blogs];
                            const sections = [...(blog.markdown_sections || [])];
                            sections[idx] = { ...section, content: e.target.value };
                            newBlogs[editingBlog] = { ...blog, markdown_sections: sections };
                            setBlogs(newBlogs);
                          }}
                          rows={section.type === 'heading' || section.type === 'subheading' ? 1 : 3}
                          className="w-full bg-neutral-900 border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none resize-none"
                          placeholder={
                            section.type === 'heading' ? 'Section heading...' :
                            section.type === 'subheading' ? 'Subheading...' :
                            section.type === 'quote' ? 'Your quote here...' :
                            section.type === 'code' ? 'Your code here...' :
                            'Write your paragraph...'
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Preview */}
                {(blog.markdown_title || blog.markdown_intro || (blog.markdown_sections || []).length > 0) && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-xs text-gray-500 hover:text-accent uppercase font-bold">
                      Preview Content
                    </summary>
                    <div className="mt-3 p-4 bg-white/5 rounded-lg border border-white/10 prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {(() => {
                          let markdown = '';
                          if (blog.markdown_title) markdown += `# ${blog.markdown_title}\n\n`;
                          if (blog.markdown_intro) markdown += `${blog.markdown_intro}\n\n`;
                          (blog.markdown_sections || []).forEach(section => {
                            if (section.type === 'heading') markdown += `## ${section.content}\n\n`;
                            else if (section.type === 'subheading') markdown += `### ${section.content}\n\n`;
                            else if (section.type === 'paragraph') markdown += `${section.content}\n\n`;
                            else if (section.type === 'list') {
                              (section.items || []).forEach(item => {
                                if (item) markdown += `- ${item}\n`;
                              });
                              markdown += '\n';
                            }
                            else if (section.type === 'numbered') {
                              (section.items || []).forEach((item, i) => {
                                if (item) markdown += `${i + 1}. ${item}\n`;
                              });
                              markdown += '\n';
                            }
                            else if (section.type === 'quote') markdown += `> ${section.content}\n\n`;
                            else if (section.type === 'code') markdown += `\`\`\`\n${section.content}\n\`\`\`\n\n`;
                          });
                          return markdown;
                        })()}
                      </ReactMarkdown>
                    </div>
                  </details>
                )}
              </div>
            ) : (
              // HTML Editor
              <textarea
                value={blog.content || ''}
                onChange={(e) => {
                  const newBlogs = [...blogs];
                  newBlogs[editingBlog] = { ...blog, content: e.target.value };
                  setBlogs(newBlogs);
                }}
                rows="15"
                className="w-full bg-black border border-white/10 p-3 rounded-lg text-white font-mono text-sm focus:border-accent outline-none resize-none"
                placeholder='<p>Your blog content here...</p>'
              />
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase">Tags (comma separated)</label>
            <input
              value={(blog.tags || []).join(', ')}
              onChange={(e) => {
                const newBlogs = [...blogs];
                newBlogs[editingBlog] = { ...blog, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) };
                setBlogs(newBlogs);
              }}
              className="w-full bg-black border border-white/10 p-3 rounded-lg text-white focus:border-accent outline-none"
              placeholder="design, web, react"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-neutral-900 rounded-xl border border-white/10">
            <div className="flex items-center gap-3">
              {blog.published ? <Eye className="w-5 h-5 text-accent" /> : <EyeOff className="w-5 h-5 text-gray-500" />}
              <div>
                <h4 className="text-white font-bold text-sm">Publish Status</h4>
                <p className="text-gray-500 text-xs">Published posts are visible on the blog page</p>
              </div>
            </div>
            <button
              onClick={() => {
                const newBlogs = [...blogs];
                newBlogs[editingBlog] = { ...blog, published: !blog.published };
                setBlogs(newBlogs);
              }}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${blog.published
                ? 'bg-accent text-black'
                : 'bg-white/10 text-gray-500'
              }`}
            >
              {blog.published ? 'Published' : 'Draft'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <h3 className="text-2xl font-bold">Blog Posts</h3>
            <p className="text-gray-500 text-sm">Create and manage blog posts for organic traffic</p>
          </div>
          <button
            onClick={() => {
              const newBlog = {
                id: `temp-${Date.now()}`,
                title: "New Blog Post",
                slug: `new-post-${Date.now()}`,
                excerpt: "",
                content: "",
                content_format: "markdown",
                markdown_title: "",
                markdown_intro: "",
                markdown_sections: [],
                cover_image: "",
                category: "",
                tags: [],
                author: "",
                read_time: null,
                published: false,
                created_at: new Date().toISOString()
              };
              setBlogs([newBlog, ...blogs]);
              setEditingBlog(0);
            }}
            className="flex items-center gap-2 bg-accent text-black px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-widest hover:bg-white transition-colors"
          >
            <Plus className="w-4 h-4" /> New Post
          </button>
        </div>

        {blogs.length === 0 ? (
          <div className="bg-neutral-900 border border-white/10 rounded-xl p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h4 className="text-white font-bold text-lg mb-2">No Blog Posts Yet</h4>
            <p className="text-gray-500 text-sm">Create your first blog post to start driving organic traffic.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {blogs.map((blog, index) => (
              <div
                key={blog.id}
                className="bg-neutral-900 border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-white/20 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {blog.cover_image ? (
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-black">
                      <img src={blog.cover_image} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-bold text-white">{blog.title}</h4>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      {blog.category && <span>{blog.category}</span>}
                      <span className={blog.published ? 'text-accent' : 'text-gray-600'}>
                        {blog.published ? '● Published' : '○ Draft'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingBlog(index)}
                    className="px-3 py-2 bg-white/10 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-white/20 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm("Delete this blog post?")) {
                        setBlogs(blogs.filter((_, i) => i !== index));
                      }
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderUsersEditor = () => (
    <div className="space-y-8">
      <div className="border-b border-white/10 pb-6">
        <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <Shield className="w-6 h-6 text-accent" /> User Management
        </h3>
        <p className="text-gray-400">Add, view, and remove admin users. Only superadmins can access this section.</p>
      </div>

      {/* Add New User Form */}
      <div className="bg-neutral-900/50 p-6 rounded-xl border border-accent/20 space-y-4">
        <h4 className="font-bold text-accent uppercase tracking-widest text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add New User
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Email</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              className="w-full bg-black p-2 rounded border border-white/10 text-white"
              placeholder="user@example.com"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Password</label>
            <input
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className="w-full bg-black p-2 rounded border border-white/10 text-white"
              placeholder="••••••••"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">Role</label>
            <select
              value={newUserRole}
              onChange={(e) => setNewUserRole(e.target.value)}
              className="w-full bg-black p-2 rounded border border-white/10 text-white"
            >
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>
        </div>
        <button
          onClick={handleAddUser}
          disabled={addingUser}
          className="bg-accent text-black px-6 py-2 rounded-lg font-bold text-sm uppercase tracking-widest hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {addingUser ? "Creating..." : "Create User"}
        </button>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        <h4 className="font-bold text-white uppercase tracking-widest text-sm">Current Users ({users.length})</h4>
        {users.map((user) => (
          <div key={user.id} className="bg-neutral-900/50 p-4 rounded-xl border border-white/5 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'superadmin' ? 'bg-accent text-black' : 'bg-white/10 text-gray-400'}`}>
                {user.role === 'superadmin' ? <Shield className="w-5 h-5" /> : <Users className="w-5 h-5" />}
              </div>
              <div>
                <p className="font-bold text-white">{user.email}</p>
                <p className="text-xs font-mono uppercase tracking-widest text-gray-500">
                  {user.role} {user.created_at && `• ${new Date(user.created_at).toLocaleDateString()}`}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleDeleteUser(user.id, user.email)}
              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-transparent hover:border-red-500/20"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white flex font-sans">
      {/* Sidebar */}
      <aside className="w-20 lg:w-72 border-r border-white/10 bg-neutral-900/50 flex flex-col fixed md:relative z-20 h-full transition-all duration-300">
        <div className="p-4 lg:p-6 border-b border-white/10 flex justify-center lg:justify-start">
          <span className="text-xl font-display font-bold text-white hidden lg:block">XLUMA</span>
          <span className="text-xl font-display font-bold text-accent lg:hidden">X</span>
        </div>
        <nav className="flex-1 p-2 lg:p-4 space-y-2">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`w-full flex items-center gap-3 px-3 lg:px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 group ${activeSection === section.id
                ? "bg-accent text-black shadow-[0_0_20px_rgba(204,255,0,0.2)]"
                : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
            >
              {section.icon}
              <div className="text-left hidden lg:block">
                <span className="block font-bold">{section.label}</span>
                <span className={`text-[10px] opacity-70 font-normal ${activeSection === section.id ? 'text-black' : 'text-gray-500 group-hover:text-gray-300'}`}>{section.description}</span>
              </div>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center lg:justify-start gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl text-sm transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden lg:block font-bold">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto w-full ml-20 lg:ml-0 bg-black">
        <header className="h-20 border-b border-white/10 flex items-center justify-between px-6 lg:px-10 bg-black/80 backdrop-blur sticky top-0 z-30">
          <div>
            <h2 className="text-xl font-bold text-white">{sections.find(s => s.id === activeSection)?.label}</h2>
            <p className="text-xs text-gray-500 hidden md:block mt-1">
              {readOnlySections.includes(activeSection) ? "Inbox and user tools do not use Save." : "Make changes below and click save."}
            </p>
          </div>
          {!readOnlySections.includes(activeSection) && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-accent text-black px-6 py-3 rounded-lg font-bold uppercase text-xs tracking-widest hover:bg-white hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:hover:scale-100 shadow-[0_0_15px_rgba(204,255,0,0.3)]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </header>

        <div className="p-6 lg:p-10 max-w-5xl mx-auto pb-32">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 text-accent gap-4">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm font-mono uppercase tracking-widest">Loading Content...</p>
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {activeSection === "hero" && renderHeroEditor()}
              {activeSection === "about" && renderAboutEditor()}
              {activeSection === "trusted_by" && renderTrustedByEditor()}
              {activeSection === "services" && renderServicesEditor()}
              {activeSection === "process" && renderProcessEditor()}
              {activeSection === "quote_requests" && renderQuoteRequestsEditor()}
              {activeSection === "contacts" && renderContactsEditor()}
              {activeSection === "quote_settings" && renderQuoteSettingsEditor()}
              {activeSection === "ticker" && renderTickerEditor()}
              {activeSection === "footer" && renderFooterEditor()}
              {activeSection === "projects" && renderProjectsEditor()}
              {activeSection === "blogs" && renderBlogsEditor()}
              {activeSection === "users" && renderUsersEditor()}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
